import { type FormEvent, type ReactNode, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { useAuth, useCan } from "../../auth/AuthProvider";
import { CopyField } from "../../components/CopyField";
import { DetailHeader } from "../../components/DetailHeader";
import { ConfirmDialog } from "../../components/Modal";
import { Icon } from "../../components/icons";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  Mono,
  StatusBadge,
  cx,
} from "../../components/ui";
import { ApiError } from "../../lib/api";
import { absoluteTime, relativeTime } from "../../lib/format";
import { formatMoney } from "../../lib/money";
import { regionLabel } from "../../lib/regions";
import {
  useAddTunnelTarget,
  useDeleteTunnel,
  useTunnelLifecycle,
  useEgress,
  useRemoveTunnelTarget,
  useTunnel,
  useTunnelConnectivity,
  useUpdateTunnel,
} from "../../lib/queries";
import type { Tunnel, TunnelTarget } from "../../lib/types";
import { stepBlocker } from "./peer";
import { tunnelStatus } from "./status";

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="micro">{label}</dt>
      <dd className="mt-1.5 text-sm text-fg">{children}</dd>
    </div>
  );
}

/**
 * One tunnel — what apps connect to, and the half of the setup that is not on
 * this platform.
 *
 * Split out of the list because a connection is two-sided and only one side is
 * here: a customer who fills in the form and changes nothing on their own
 * endpoint gets a tunnel that never carries traffic and no error anywhere
 * explaining it. That handover is three panels of reading, which belongs on a
 * page somebody opened rather than repeated down a list they were scanning.
 *
 * It wears the header apps and databases wear, so the hostname sits under the
 * title where every other address on this platform sits, and the state reads
 * as a badge beside the name rather than as a cell in a table of facts.
 *
 * It is also where connectivity is asked for. The reading costs an engine
 * round trip per tunnel, so a list of twenty must not take it and a page about
 * one of them must.
 */
export function TunnelDetailPage() {
  const { tunnelId } = useParams();
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const canManage = useCan("admin");
  const navigate = useNavigate();

  const { data, isPending, error } = useTunnel(orgId, tunnelId);
  const link = useTunnelConnectivity(orgId, tunnelId);
  const remove = useDeleteTunnel(orgId ?? "");
  const lifecycle = useTunnelLifecycle(orgId ?? "");

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  /*
   * Above the early returns, like every other hook here. It used to sit below
   * them, so the loading render called one hook fewer than the loaded one and
   * React threw #310 on every visit that started from a pending query.
   */
  const { t } = useTranslation("tunnels");

  if (isPending) return <LoadingBlock />;
  if (error) return <GoneOrBroken error={error} />;
  if (!data) return <GoneOrBroken error={null} />;

  const tunnel = data.data;
  const targets = data.targets;
  /*
   * From the response, never from a constant here. The add-on is repriceable
   * from the operator console and §6.5 gives customers 30 days' notice of a
   * rise — a page holding its own $5.00 would go on quoting the old number
   * through the notice period and after it.
   */
  const monthly = `$${(data.price.monthlyCents / 100).toFixed(2)}`;
  const status = tunnelStatus(tunnel.state, link.data?.connectivity);
  const region = regionLabel(tunnel.region) ?? tunnel.region;

  /*
   * Whether the far side is answering, which is what decides how much of this
   * page is about setup. Only `healthy` counts: a tunnel that has never
   * handshaken and one whose endpoint has gone away both need the same three
   * things checked, and neither is finished.
   */
  /*
   * The two states the guide is written for, named rather than derived from the
   * absence of the third.
   *
   * `unknown` is deliberately not one of them. It means the engine could not be
   * asked, or there is no container yet — and this codebase says in the API, the
   * docs and the query hook that unknown is never rendered as a problem.
   * Putting a setup guide on a working tunnel because a server was briefly
   * unreachable would be exactly that.
   *
   * It also removes a flash: before the first runtime response `connectivity`
   * is undefined, which matches neither value, so a healthy tunnel never paints
   * the guide on its way to finding out.
   */
  const needsSetup =
    link.data?.connectivity === "starting" || link.data?.connectivity === "unhealthy";

  async function destroy() {
    if (!tunnelId) return;
    await remove.mutateAsync(tunnelId);
    navigate("/tunnels", {
      state: { notice: t("detail.deleted", { name: tunnel.name }) },
    });
  }

  return (
    <div className="space-y-6">
      <DetailHeader
        backTo="/tunnels"
        backLabel="Tunnels"
        name={tunnel.name}
        /*
         * Stale when the engine was asked and could not say, which is what
         * `unknown` means — the badge is then showing the stored state, and
         * that is a record rather than a reading.
         *
         * Not while the read is merely in flight. The detail route serialises
         * the row as stored, so `tunnel.stale` is always true here; pairing it
         * with an undefined runtime answer tagged every tunnel "(last known)"
         * for the first paint of every visit.
         */
        badge={
          <StatusBadge
            tone={status.tone}
            label={t(`state.${status.key}.label`)}
            title={t(`state.${status.key}.hint`)}
            stale={link.data?.connectivity === "unknown"}
          />
        }
        /*
         * The hostname, where an app's live URL and a database's internal
         * address sit. Not a link: it resolves on the placement's own network
         * and nowhere a browser can reach, so offering a click would offer one
         * that cannot work. Copyable, because pasting it into a connection
         * string is the whole point.
         */
        address={{
          value: tunnel.internalHost ?? "",
          copyLabel: t("detail.copyHostname"),
          empty: <span className="text-sm text-fg-subtle">{t("detail.noHostname")}</span>,
        }}
        /*
         * The control a connection did not have until running out of credit
         * had to be able to stop one. Starting is the half that matters here:
         * the hourly run stops every workload at a zero balance and a top-up
         * restores none of them, so this is how somebody who has paid brings
         * the connection their apps depend on back.
         *
         * Only once there is a container. Before that the row is real and the
         * connection is not, and the page already says so in its own words —
         * offering Start there would offer a button with nothing to start.
         */
        actions={
          canManage &&
          tunnel.provisioned && (
            <Button
              variant="secondary"
              loading={lifecycle.isPending}
              onClick={() =>
                lifecycle.mutate({
                  id: tunnel.id,
                  action: tunnel.state === "stopped" ? "start" : "stop",
                })
              }
            >
              {tunnel.state === "stopped" ? t("detail.start") : t("detail.stop")}
            </Button>
          )
        }
        meta={[
          { label: t("detail.region"), value: region ?? "—" },
          {
            label: t("detail.addresses"),
            value: targets.length === 0 ? t("noneYet") : String(targets.length),
          },
          {
            label: t("detail.cost"),
            value: tunnel.provisioned ? (
              <Trans
                i18nKey="detail.monthlyMuted"
                ns="tunnels"
                values={{ amount: monthly }}
                components={{ muted: <span className="text-fg-subtle" /> }}
              />
            ) : (
              <span className="text-fg-subtle">{t("notBilled")}</span>
            ),
          },
          {
            label: t("detail.created"),
            value: <span title={absoluteTime(tunnel.createdAt)}>{relativeTime(tunnel.createdAt)}</span>,
          },
        ]}
      />

      {remove.error && <Alert>{remove.error.message}</Alert>}
      {lifecycle.error && <Alert>{lifecycle.error.message}</Alert>}

      {/*
        The state as a sentence, and only when the state is worth a sentence.
        A badge saying "Not connected" is a label; what somebody needs is what
        it means for their apps, which is the difference between reading a
        status and understanding it. Nothing is said while it is connected —
        the badge already covers the case with no consequences.
      */}
      {tunnel.provisioned && status.tone !== "good" && (
        <Alert tone={status.tone === "bad" ? "error" : "warning"}>
          {t("detail.stateLine", {
            label: t(`state.${status.key}.label`),
            hint: t(`state.${status.key}.hint`),
          })}
        </Alert>
      )}

      {/*
        A row that never reached the engine. Says both halves, because a
        customer looking at a tunnel that does nothing wants to know whether
        they are paying for it: they are not, and the fix is to delete it.
      */}
      {!tunnel.provisioned && (
        <Alert>{t("detail.unprovisioned")}</Alert>
      )}

      {/*
        The setup guide, and only while it is the next thing to do.
        A tunnel that is not connected will not be fixed by adding another
        address to it, so the handover sits above the addresses; once the far
        side is answering the three steps are done and the panel goes, rather
        than staying as a fold nobody opens again.

        Gated on the read having finished, not on its absence. `connected` is
        false before the first runtime response as well as after a bad one, so
        testing it alone flashed the whole guide onto every healthy tunnel for
        one paint.
      */}
      {needsSetup && <FinishOnYourSide tunnel={tunnel} />}

      <Targets
        orgId={orgId ?? ""}
        tunnel={tunnel}
        targets={targets}
        canManage={canManage}
      />

      {canManage && <Settings orgId={orgId ?? ""} tunnel={tunnel} />}


      {/*
        What it is priced at against what it has actually taken, which a
        scheduled job has carried since it shipped and a tunnel — billed
        identically, from the same add-on table — did not. A flat monthly price
        is easy to reason about until somebody wants to know what a connection
        they created mid-month has cost them.
      */}
      <Card>
        <CardHeader
          title={t("detail.costTitle")}
          description={t("detail.costLead")}
        />
        <dl className="grid grid-cols-2 gap-x-6 gap-y-5 p-5 sm:grid-cols-3">
          <Stat label={t("detail.price")}>
            <Trans
              i18nKey="detail.monthly"
              ns="tunnels"
              values={{ amount: monthly }}
              components={{ amount: <span className="tabular-nums" /> }}
            />
          </Stat>
          <Stat label={t("detail.thisMonth")}>
            <span className="tabular-nums">{formatMoney(data.costToDate.monthMicros)}</span>
          </Stat>
          <Stat label={t("detail.sinceCreated")}>
            <span className="tabular-nums">{formatMoney(data.costToDate.sinceCreatedMicros)}</span>
          </Stat>
        </dl>
      </Card>

      {canManage && (
        /*
         * A row, not a card with a header. Deleting is one sentence and one
         * button, and giving it the same furniture as the panels above put a
         * destructive act on equal footing with the page's actual content.
         */
        <div className="flex flex-wrap items-center justify-between gap-4 border border-danger/20 px-5 py-4">
          <p className="text-[13px] text-fg-muted">
            {t("detail.deleteLine", { amount: monthly })}
          </p>
          <Button variant="danger" size="sm" onClick={() => setConfirmingDelete(true)}>
            {t("detail.delete")}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        title={t("detail.deleteTitle", { name: tunnel.name })}
        confirmLabel={t("detail.delete")}
        loading={remove.isPending}
        description={
          targets.length > 0
            ? t("detail.deleteAddresses", { count: targets.length })
            : t("detail.deleteLead")
        }
        onConfirm={() => void destroy()}
      />
    </div>
  );
}

/**
 * An error where the page's own back link is not rendered.
 *
 * A 404 here is not the story `vanishedWhileCreating` tells about apps and
 * databases: the API never reconciles a tunnel away, and a create that failed
 * leaves the row at `error` rather than removing it. So the only way one of
 * these disappears is somebody deleting it — from another tab, or a colleague —
 * and the useful thing to say is that it is gone, with the way back.
 */
export function GoneOrBroken({ error }: { error: unknown }) {
  const { t } = useTranslation("tunnels");
  const gone = error === null || (error instanceof ApiError && error.status === 404);
  return (
    <Alert tone={gone ? "info" : "error"}>
      <span>
        {gone
          ? t("detail.gone")
          : error instanceof Error
            ? error.message
            : t("detail.loadFailed")}{" "}
        <Link to="/tunnels" className="underline">
          {t("detail.back")}
        </Link>
      </span>
    </Alert>
  );
}

/**
 * The addresses, which are the whole product.
 *
 * Each one renders as the string that goes in a connection string, because that
 * is what somebody came here to get — the remote address it maps to is the
 * caption, not the headline.
 */
function Targets({
  orgId,
  tunnel,
  targets,
  canManage,
}: {
  orgId: string;
  tunnel: Tunnel;
  targets: TunnelTarget[];
  canManage: boolean;
}) {
  const { t } = useTranslation("tunnels");
  const addTarget = useAddTunnelTarget(orgId, tunnel.id);
  const removeTarget = useRemoveTunnelTarget(orgId, tunnel.id);

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [remoteHost, setRemoteHost] = useState("");
  const [remotePort, setRemotePort] = useState("5432");
  const [error, setError] = useState<string | null>(null);

  const open = adding || targets.length === 0;

  function onAdd(event: FormEvent) {
    event.preventDefault();
    setError(null);
    addTarget.mutate(
      { name, remoteHost: remoteHost.trim(), remotePort: Number(remotePort) },
      {
        onSuccess: () => {
          setName("");
          setRemoteHost("");
          setAdding(false);
        },
        onError: (err) => setError(err.message),
      },
    );
  }

  return (
    <Card>
      <CardHeader
        title={t("detail.targets.title")}
        description={t("detail.targets.lead")}
        action={
          canManage && targets.length > 0 && !open ? (
            <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
              <Icon name="plus" className="size-3.5" />
              {t("detail.targets.add")}
            </Button>
          ) : undefined
        }
      />

      {targets.length === 0 && !canManage ? (
        <EmptyState
          icon="lock"
          title={t("detail.targets.empty")}
          description={t("detail.targets.emptyLead")}
        />
      ) : (
        <ul className="divide-y divide-line">
          {targets.map((target) => (
            <li key={target.id} className="space-y-3 px-5 py-4">
              <div className="flex items-center justify-between gap-3">
                <p className="min-w-0 truncate text-sm font-medium text-fg">{target.name}</p>
                {canManage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTarget.mutate(target.id)}
                    loading={removeTarget.isPending}
                  >
                    {t("detail.targets.remove")}
                  </Button>
                )}
              </div>

              {/*
                The row as the mapping it is: what an app connects to on the
                left, what that reaches on the right. The connection string
                comes first in both directions — reading order and visual
                weight — because it is the thing somebody came here to copy.
              */}
              <div className="grid gap-3 sm:grid-cols-[minmax(0,22rem)_auto_minmax(0,1fr)] sm:items-center">
                <CopyField
                  label={t("detail.targets.addressFor", { name: target.name })}
                  value={`${tunnel.internalHost ?? tunnel.slug}:${target.listenPort}`}
                />
                <span aria-hidden="true" className="hidden text-fg-subtle sm:inline">
                  &rarr;
                </span>
                <p className="min-w-0 text-xs text-fg-subtle">
                  <span className="sm:hidden">&rarr; </span>
                  <Mono>
                    {target.remoteHost}:{target.remotePort}
                  </Mono>{" "}
                  {t("detail.targets.inYourNetwork")}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/*
        Said once, above the form that fixes it. A tunnel with no addresses is
        up and carrying nothing, which looks identical to a working one from
        every other panel on this page.
      */}
      {canManage && targets.length === 0 && (
        <p className="border-b border-line px-5 py-3 text-sm text-fg-subtle">
          {t("detail.targets.nothingYet")}
        </p>
      )}

      {canManage && open && (
        <form
          onSubmit={onAdd}
          className={cx(
            "grid gap-3 px-5 py-4 sm:grid-cols-[1fr_1fr_6rem_auto] sm:items-end",
            targets.length > 0 && "border-t border-line bg-raised/30",
          )}
        >
          {/*
            One line above the row rather than a hint under two of the fields.
            `Field` renders its hint below the control, so a hinted field is
            taller than an unhinted one — and `items-end` then aligned three
            inputs at three different heights. The guidance was right and the
            place was wrong.

            It also stops the placeholder having to carry the breadth on its
            own: a vague placeholder helps nobody name the common case, and a
            database-shaped one reads as the limit, which it never was.
          */}
          <p className="text-[13px] text-fg-subtle sm:col-span-4">
            {t("detail.targets.formLead")}
          </p>

          <Field label={t("detail.targets.name")} htmlFor={`n-${tunnel.id}`}>
            <Input
              id={`n-${tunnel.id}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("detail.targets.namePlaceholder")}
              required
            />
          </Field>
          <Field label={t("detail.targets.host")} htmlFor={`h-${tunnel.id}`}>
            <Input
              id={`h-${tunnel.id}`}
              value={remoteHost}
              onChange={(e) => setRemoteHost(e.target.value)}
              placeholder="10.0.4.12"
              spellCheck={false}
              required
            />
          </Field>
          <Field label={t("detail.targets.port")} htmlFor={`p-${tunnel.id}`}>
            <Input
              id={`p-${tunnel.id}`}
              type="number"
              value={remotePort}
              onChange={(e) => setRemotePort(e.target.value)}
              required
            />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" variant="primary" loading={addTarget.isPending}>
              {t("detail.targets.addAction")}
            </Button>
            {targets.length > 0 && (
              <Button type="button" variant="secondary" onClick={() => setAdding(false)}>
                {t("detail.targets.cancel")}
              </Button>
            )}
          </div>
        </form>
      )}

      {error && (
        <div className="border-t border-line px-5 py-4">
          <Alert>{error}</Alert>
        </div>
      )}

      {/*
        Both writes republish the container, which the API documents and this
        screen has to as well: somebody adding a second database at midday would
        otherwise take the first one offline for a few seconds with nothing
        having warned them.
      */}
      {canManage && targets.length > 0 && (
        <p className="border-t border-line px-5 py-3 text-xs text-fg-subtle">
          {t("detail.targets.restartNote")}
        </p>
      )}
    </Card>
  );
}

/**
 * Renaming a connection, and replacing the credential behind it.
 *
 * Rotation is the one maintenance task a WireGuard peer has, and until this
 * panel the only way to do it was to delete the connection and build another —
 * which changes the hostname every app's connection string names and drops
 * every address on it. The API had carried the operation since tunnels shipped;
 * nothing called it.
 *
 * The peer fields start empty, and that is not an oversight. Nothing here ever
 * reads back a stored credential — the detail response carries the public key
 * and nothing else — so this replaces the peer wholesale rather than editing
 * one field of it, and the wording says "replace" for that reason.
 *
 * The two halves save separately because they cost different things. A rename
 * changes a label; replacing the peer republishes the container and drops every
 * connection through it for a few seconds, and putting both behind one button
 * would make the cheap edit carry the expensive one's consequence.
 */
function Settings({ orgId, tunnel }: { orgId: string; tunnel: Tunnel }) {
  const { t } = useTranslation("tunnels");
  // The example endpoint is the wizard's, read from its namespace rather than copied.
  const { t: tc } = useTranslation("create");
  const update = useUpdateTunnel(orgId, tunnel.id);

  const [name, setName] = useState(tunnel.name);
  const [replacing, setReplacing] = useState(false);
  const [peer, setPeer] = useState({
    endpoint: "",
    peerPublicKey: "",
    privateKey: "",
    address: "",
    allowedIps: "10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16",
  });
  const [notice, setNotice] = useState<string | null>(null);

  const renamed = name.trim().length > 0 && name.trim() !== tunnel.name;

  /*
   * The wizard's own validator, on a draft carrying the name it already has.
   * The rules for a peer do not change because the connection exists, and two
   * copies of them would drift — this is the one the create page is tested
   * against.
   */
  const blocker =
    stepBlocker(0, { ...peer, name: tunnel.name, allowedIps: peer.allowedIps }) ??
    stepBlocker(1, { ...peer, name: tunnel.name });

  function saveName(event: FormEvent) {
    event.preventDefault();
    setNotice(null);
    update.mutate(
      { name: name.trim() },
      { onSuccess: () => setNotice(t("detail.settings.renamed")) },
    );
  }

  function replacePeer(event: FormEvent) {
    event.preventDefault();
    setNotice(null);
    update.mutate(
      {
        peer: {
          privateKey: peer.privateKey.trim(),
          address: peer.address.trim(),
          peerPublicKey: peer.peerPublicKey.trim(),
          endpoint: peer.endpoint.trim(),
          allowedIps: peer.allowedIps
            .split(",")
            .map((cidr) => cidr.trim())
            .filter(Boolean),
        },
      },
      {
        onSuccess: () => {
          setReplacing(false);
          setPeer({ ...peer, privateKey: "", peerPublicKey: "", address: "", endpoint: "" });
          setNotice(t("detail.settings.replaced"));
        },
      },
    );
  }

  return (
    <Card>
      <CardHeader
        title={t("detail.settings.title")}
        description={t("detail.settings.lead")}
      />

      {update.error && (
        <div className="border-b border-line px-5 py-4">
          <Alert>{update.error.message}</Alert>
        </div>
      )}
      {notice && (
        <div className="border-b border-line px-5 py-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}

      {/*
        The field sized to what goes in it, not to the card. A name is sixty
        characters and the card is a thousand pixels wide — stretched across it
        the input reads as a paragraph box, and the button it pairs with ends up
        at the far edge of the screen from the text it acts on.
      */}
      <form
        onSubmit={saveName}
        className="grid gap-3 px-5 py-4 sm:grid-cols-[minmax(0,22rem)_auto_1fr] sm:items-end"
      >
        <Field label={t("detail.settings.name")} htmlFor={`name-${tunnel.id}`}>
          <Input
            id={`name-${tunnel.id}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
          />
        </Field>
        <Button type="submit" variant="secondary" disabled={!renamed} loading={update.isPending}>
          {t("detail.settings.rename")}
        </Button>
      </form>

      <div className="border-t border-line px-5 py-4">
        {!replacing ? (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-fg">{t("detail.settings.peer")}</p>
              <p className="mt-1 text-[13px] text-fg-subtle">{t("detail.settings.peerLead")}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setReplacing(true)}>
              {t("detail.settings.replace")}
            </Button>
          </div>
        ) : (
          <form onSubmit={replacePeer} className="space-y-5">
            {/*
              Capped, like the copy fields in the setup guide above it. These
              hold a host:port and three base64 keys — none of them wider than a
              terminal line — and stretched across a full-width card they read
              as somewhere a paragraph goes. The action row below stays full
              width, so the buttons keep the edge every other form here uses.
            */}
            <div className="max-w-xl space-y-5">
              {/*
                Said before the fields, not after them. Every connection through
                this tunnel drops when it saves, and somebody who learns that from
                a confirmation dialog has already typed five values.
              */}
              <div className="space-y-1">
                <p className="text-sm font-medium text-fg">
                  {t("detail.settings.replaceTitle")}
                </p>
                <p className="text-[13px] leading-5 text-fg-subtle">
                  {t("detail.settings.replaceLead")}
                </p>
              </div>

              <Field
                label={t("detail.settings.endpoint")}
                htmlFor={`ep-${tunnel.id}`}
                hint={t("detail.settings.endpointHint")}
              >
                <Input
                  id={`ep-${tunnel.id}`}
                  value={peer.endpoint}
                  onChange={(e) => setPeer({ ...peer, endpoint: e.target.value })}
                  placeholder={tc("tunnel.endpointPlaceholder")}
                  spellCheck={false}
                />
              </Field>
              <Field
                label={t("detail.settings.publicKey")}
                htmlFor={`pub-${tunnel.id}`}
                hint={t("detail.settings.publicKeyHint")}
              >
                <Input
                  id={`pub-${tunnel.id}`}
                  value={peer.peerPublicKey}
                  onChange={(e) => setPeer({ ...peer, peerPublicKey: e.target.value })}
                  autoComplete="off"
                  spellCheck={false}
                />
              </Field>
              <Field
                label={t("detail.settings.privateKey")}
                htmlFor={`key-${tunnel.id}`}
                hint={t("detail.settings.privateKeyHint")}
              >
                <Input
                  id={`key-${tunnel.id}`}
                  value={peer.privateKey}
                  onChange={(e) => setPeer({ ...peer, privateKey: e.target.value })}
                  autoComplete="off"
                  spellCheck={false}
                />
              </Field>
              <Field label={t("detail.settings.address")} htmlFor={`addr-${tunnel.id}`}>
                <Input
                  id={`addr-${tunnel.id}`}
                  value={peer.address}
                  onChange={(e) => setPeer({ ...peer, address: e.target.value })}
                  placeholder="10.0.0.2/32"
                  spellCheck={false}
                />
              </Field>
              <Field label={t("detail.settings.allowed")} htmlFor={`allowed-${tunnel.id}`}>
                <Input
                  id={`allowed-${tunnel.id}`}
                  value={peer.allowedIps}
                  onChange={(e) => setPeer({ ...peer, allowedIps: e.target.value })}
                  spellCheck={false}
                />
              </Field>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-5">
              {/* The reason, not a disabled button that looks broken. */}
              <p className="text-xs text-fg-subtle">{blocker ?? " "}</p>
              <div className="flex gap-2">
                <Button type="button" variant="secondary" onClick={() => setReplacing(false)}>
                  {t("detail.settings.cancel")}
                </Button>
                <Button type="submit" disabled={Boolean(blocker)} loading={update.isPending}>
                  {t("detail.settings.save")}
                                    </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}

/**
 * The half of the setup that is not on this platform.
 *
 * A connection is two-sided and only one side is here, so a customer who fills
 * in the form and changes nothing on their endpoint gets a tunnel that never
 * carries traffic and no error anywhere explaining it. The outbound address is
 * first because it is the most common cause: we dial *out* to their endpoint,
 * from an address their firewall has never seen.
 *
 * Open while the far side is silent, folded once it answers. A handshake is
 * proof all three of these were done, so leaving the panel open afterwards
 * would be asking somebody to keep reading instructions they have finished —
 * and closing it entirely would hide the values they need when it breaks.
 */
function FinishOnYourSide({ tunnel }: { tunnel: Tunnel }) {
  const { t } = useTranslation("tunnels");
  const { currentOrg } = useAuth();
  const egress = useEgress(currentOrg?.id);

  // The address this tunnel's own placement calls out from. A tenant in two
  // regions calls out from two, and the wrong one allowlisted is a tunnel that
  // never comes up.
  const outbound = egress.data?.data.find((entry) => entry.region === tunnel.region);

  const steps = [
    {
      key: "reach",
      field: outbound?.address
        ? { label: t("detail.handover.reachField"), value: outbound.address }
        : null,
      missing: t("detail.handover.reachMissing"),
    },
    {
      key: "peer",
      field: tunnel.publicKey
        ? { label: t("detail.handover.peerField"), value: tunnel.publicKey }
        : null,
      missing: null,
    },
    { key: "route", field: null, missing: null },
  ] as const;

  return (
    <Card>
      <CardHeader
        title={t("detail.handover.title")}
        description={t("detail.handover.lead")}
      />

      {/*
        Down the page rather than across it.
        Three columns fitted the width this was designed against and not the
        width it runs at: the rail takes 260px from `lg` up, so the content
        column is at its narrowest exactly where `md:grid-cols-3` switches on.
        Each column came out around 230px, which truncated a 44-character
        public key to `ZyXwVuTsRqPoNml` — a value whose whole purpose is being
        checked against the one on the far side.

        Stacking also fixes what the columns did to the reading: three cells of
        unequal text left the copy fields at three different heights, and these
        are steps, which are read in order anyway.
      */}
      <ol className="divide-y divide-line border-t border-line">
        {steps.map((step, index) => (
          <li key={step.key} className="grid gap-x-4 gap-y-2 p-5 sm:grid-cols-[2.5rem_1fr]">
            <p className="micro text-fg-subtle sm:pt-0.5">
              {t("detail.handover.step", { number: index + 1 })}
            </p>
            <div className="min-w-0 space-y-2">
              <p className="text-sm font-medium text-fg">
                {t(`detail.handover.${step.key}` as "detail.handover.peer")}
              </p>
              <p className="text-xs leading-5 text-fg-subtle">
                {t(`detail.handover.${step.key}Detail` as "detail.handover.peerDetail")}
              </p>
              {step.field ? (
                /* Wide enough for a full base64 key beside its Copy button,
                   and capped so it does not stretch across a wide screen. */
                <div className="max-w-lg pt-1">
                  <CopyField label={step.field.label} value={step.field.value} />
                </div>
              ) : (
                step.missing && <p className="text-xs text-fg-subtle">{step.missing}</p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}
